import axios from 'axios';
import _ from 'lodash';
import {
  getBaseContentUrl,
  helperGetApiHeaders,
  helperGetApiRouteById,
  helperGetLiveUpNextUrl,
  helperConvertApiRoute,
  apiErrorHandler,
} from 'helpers/ApiHelper';
import { getAuthForPanelVideo, isLive as isTypeLive } from 'helpers/ContentHelper';

export const CLEAR_GLOBAL_LIVE_RESTART = 'CLEAR_GLOBAL_LIVE_RESTART';
export const CLEAR_GLOBAL_NEXT_VIDEOS_VALID_FOR = 'CLEAR_GLOBAL_NEXT_VIDEOS_VALID_FOR';
export const CLEAR_GLOBAL_PERCENT_WATCHED = 'CLEAR_GLOBAL_PERCENT_WATCHED';
export const CLEAR_GLOBAL_VIDEO = 'CLEAR_GLOBAL_VIDEO';
export const FETCH_GLOBAL_VIDEO = 'FETCH_GLOBAL_VIDEO';
export const FETCH_GLOBAL_VIDEO_NEXT_VIDEO = 'FETCH_GLOBAL_VIDEO_NEXT_VIDEO';
export const FETCH_GLOBAL_VIDEO_PANEL = 'FETCH_GLOBAL_VIDEO_PANEL';
export const FETCH_GLOBAL_VIDEO_UP_NEXT = 'FETCH_GLOBAL_VIDEO_UP_NEXT';
export const PLAY_GLOBAL_VIDEO_NEXT_PLAYLIST_VIDEO = 'SET_GLOBAL_VIDEO_NEXT_PLAYLIST_VIDEO';
export const RESET_VOD_SECTION_CLICKED = 'RESET_VOD_SECTION_CLICKED';
export const SAVE_GLOBAL_PERCENT_WATCHED = 'SAVE_GLOBAL_PERCENT_WATCHED';
export const SET_GLOBAL_PORTAL_REF = 'SET_GLOBAL_PORTAL_REF';
export const SET_GLOBAL_PORTAL_CONTENT_FINISHED = 'SET_GLOBAL_PORTAL_CONTENT_FINISHED';
export const SET_GLOBAL_VIDEO = 'SET_GLOBAL_VIDEO';
export const SET_GLOBAL_VIDEO_DISABLE_RENDER = 'SET_GLOBAL_VIDEO_DISABLE_RENDER';
export const SET_GLOBAL_VIDEO_ERROR = 'SET_GLOBAL_VIDEO_ERROR';
export const SET_GLOBAL_VIDEO_RETRY_ERROR = 'SET_GLOBAL_VIDEO_RETRY_ERROR';
export const SET_GLOBAL_VIDEO_PANEL = 'SET_GLOBAL_VIDEO_PANEL';
export const SET_GLOBAL_VIDEO_PLAYER_TYPE = 'SET_GLOBAL_VIDEO_PLAYER_TYPE';
export const SET_GLOBAL_VIDEO_SCREEN_IN_CONTROL_OF_STATE =
  'SET_GLOBAL_VIDEO_SCREEN_IN_CONTROL_OF_STATE';
export const SET_GLOBAL_VIDEO_SHOULD_RESET_STILL_WATCHING =
  'SET_GLOBAL_VIDEO_SHOULD_RESET_STILL_WATCHING';
export const SET_GLOBAL_VIDEO_SHOW_CONTROLS = 'SET_GLOBAL_VIDEO_SHOW_CONTROLS';
export const SET_GLOBAL_LIVE_RESTART = 'SET_GLOBAL_LIVE_RESTART';
export const SET_GLOBAL_VIDEO_STARTED_PLAYBACK = 'SET_GLOBAL_VIDEO_STARTED_PLAYBACK';
export const SET_GLOBAL_STREAM_EXIT = 'SET_GLOBAL_STREAM_EXIT';
export const HANDLE_VOD_SECTION_CLICKED = 'HANDLE_VOD_SECTION_CLICKED';
export const SET_GLOBAL_PERCENT_WATCHED_50_PERCENT = 'SET_GLOBAL_PERCENT_WATCHED_50_PERCENT';

/**
 *
 * @param {Object} panel
 * @param {Object} video
 * @param {String} source
 * @returns {{payload: {source: string}, type: string}}
 */
export function setVideoPanel({ panel, video, source = 'self' } = {}) {
  const firstItem = video || _.get(panel, 'items.member.0', null);
  const payload = { source };
  if (firstItem) payload.panel = firstItem;

  return {
    type: SET_GLOBAL_VIDEO_PANEL,
    payload,
  };
}

export function setDisableRender({ value, source = 'self', playerType, context }) {
  return {
    type: SET_GLOBAL_VIDEO_DISABLE_RENDER,
    payload: { context, value, source, playerType },
  };
}

export function setVideoStartedPlayback(value) {
  return {
    type: SET_GLOBAL_VIDEO_STARTED_PLAYBACK,
    payload: { value },
  };
}

export function fetchVideoPanel(id, accessToken, deviceId) {
  const pageType = 'videoDetail';
  const request = id
    ? axios
        .get(
          helperGetApiRouteById(pageType, id, '{videoId}'),
          helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
        )
        .catch(e => apiErrorHandler({ error: e }))
    : Promise.resolve();

  return {
    type: FETCH_GLOBAL_VIDEO_PANEL,
    payload: request,
  };
}

function setVideo(video) {
  return {
    type: SET_GLOBAL_VIDEO,
    payload: video,
  };
}

export function setPortalContentFinished(isContentFinished) {
  return {
    type: SET_GLOBAL_PORTAL_CONTENT_FINISHED,
    payload: isContentFinished,
  };
}

export function clearVideo({ source = 'self' } = {}) {
  return {
    type: CLEAR_GLOBAL_VIDEO,
    payload: { source },
  };
}

export function setError(errors) {
  return {
    type: SET_GLOBAL_VIDEO_ERROR,
    payload: errors,
  };
}

export function handleVodSectionClicked(triggerData) {
  return {
    type: HANDLE_VOD_SECTION_CLICKED,
    payload: triggerData,
  };
}

export function resetVodSectionClicked() {
  return {
    type: RESET_VOD_SECTION_CLICKED,
  };
}

export function fetchUpNext({ upNextUrl, videoId, accessToken, deviceId }) {
  let url = upNextUrl;
  let pageType = 'videoDetail';
  if (!url && videoId) {
    const data = helperGetLiveUpNextUrl({ videoId });
    ({ url, pageType } = data);
  }
  const request = url
    ? axios
        .get(
          helperConvertApiRoute(url),
          helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
        )
        .catch(e => apiErrorHandler({ error: e }))
    : Promise.resolve();

  return {
    type: FETCH_GLOBAL_VIDEO_UP_NEXT,
    payload: request,
  };
}

export function fetchVideo({ accessToken, isLive, deviceId, panelVideo }) {
  return dispatch => {
    const type = _.get(panelVideo, '@type');
    const isLiveType = isTypeLive(type) || isLive || panelVideo?.isLive;

    const { id: videoId, requiresMVPDAuth } = panelVideo;
    const newPanelVideo = panelVideo;
    const requiresAuthLive = isLive && getAuthForPanelVideo(panelVideo, isLiveType, accessToken);
    const requiresAuthVod =
      !isLiveType && requiresMVPDAuth && getAuthForPanelVideo(panelVideo, isLiveType, accessToken);
    newPanelVideo.requiresAuthLive = requiresAuthLive;
    newPanelVideo.requiresAuth = requiresAuthVod;

    const baseUrl = getBaseContentUrl();
    const upNextUrl = isLiveType
      ? `${baseUrl}upnext/epglistings/${videoId}/videos`
      : `${baseUrl}upnext/videos/${videoId}`;

    if (newPanelVideo) {
      dispatch(setVideo(newPanelVideo));
      if (upNextUrl) dispatch(fetchUpNext({ upNextUrl, accessToken, deviceId }));
    }
  };
}

export function playNextPlaylistVideo() {
  return {
    type: PLAY_GLOBAL_VIDEO_NEXT_PLAYLIST_VIDEO,
  };
}

export function fetchNextVideo(url, accessToken, deviceId) {
  const pageType = 'videoDetail';
  const request = url
    ? axios
        .get(
          helperConvertApiRoute(url),
          helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId }),
        )
        .catch(e => apiErrorHandler({ error: e }))
    : Promise.resolve();

  return {
    type: FETCH_GLOBAL_VIDEO_NEXT_VIDEO,
    payload: request,
  };
}

export function setPlayerType(value) {
  /*
    There is a new method `player.updatePlayerType(TYPE)` you can call for uninterrupted playback
    It will trigger an event called `onPlayerTypeChange` that you can listen to.
    The player listens to it to update mute state and controls
  */
  return {
    type: SET_GLOBAL_VIDEO_PLAYER_TYPE,
    payload: value,
  };
}

export function saveLiveRestart(video) {
  return {
    type: SET_GLOBAL_LIVE_RESTART,
    payload: {
      data: video,
    },
  };
}

export function clearLiveRestart() {
  return {
    type: CLEAR_GLOBAL_LIVE_RESTART,
  };
}

export function setStreamExit(streamExit) {
  return {
    type: 'SET_GLOBAL_STREAM_EXIT',
    payload: streamExit,
  };
}

export function clearNextVideosValidFor() {
  return {
    type: CLEAR_GLOBAL_NEXT_VIDEOS_VALID_FOR,
  };
}

export function setScreenInControlOfState(name) {
  /*
    to prevent race conditions with data fetches on screen mount we set control of the player.
    previously we saw that switching screens quickly leads to the actions from the previous
    screen resolving after the screen changes which causes player updates on the subsequent screen.
    for example: setting collection panels + prefetches and then the global video panel is set AFTER
    transitioning to the live screen. to resolve this immediately set screen control on mount of the
    screen.
  */
  return {
    type: SET_GLOBAL_VIDEO_SCREEN_IN_CONTROL_OF_STATE,
    payload: name,
  };
}

export function savePercentWatched() {
  return {
    type: SAVE_GLOBAL_PERCENT_WATCHED,
  };
}

export function clearPercentWatched() {
  return {
    type: CLEAR_GLOBAL_PERCENT_WATCHED,
  };
}

export function setHasSeenHalfAnyVideo() {
  return {
    type: SET_GLOBAL_PERCENT_WATCHED_50_PERCENT,
  };
}
